/**
 * Login View - Constants
 * ======================
 * Textos por defecto (mockup) y logos de marca (Apple/Google) usados por los
 * botones sociales. Estos SVG son marcas propias de terceros — NO van al
 * registro de íconos del sistema (`src/icons`), viven solo acá.
 */

// ============================================================================
// TYPES
// ============================================================================

export interface LoginTexts {
  title: string;
  subtitle: string;
  appleText: string;
  googleText: string;
  dividerText: string;
  emailPlaceholder: string;
  passwordPlaceholder: string;
  submitText: string;
  forgotText: string;
  registerQuestion: string;
  registerText: string;
  termsPrefix: string;
  termsLinkText: string;
  termsConnector: string;
  privacyLinkText: string;
}

export interface DefaultValues extends LoginTexts {
  showApple: boolean;
  showGoogle: boolean;
  showTerms: boolean;
}

// ============================================================================
// DEFAULTS (mockup)
// ============================================================================

export const DEFAULTS: DefaultValues = {
  title: 'Ingresa',
  subtitle: 'Reserva tu cancha en segundos',
  appleText: 'Continua con Apple',
  googleText: 'Continua con Google',
  dividerText: 'O con tu correo electrónico',
  emailPlaceholder: 'correo@electronico.cl',
  passwordPlaceholder: 'Contraseña',
  submitText: 'Log in',
  forgotText: '¿Olvidaste tu contraseña?',
  registerQuestion: '¿No tienes cuenta?',
  registerText: 'Regístrate',
  // Textos legales SIN default: el footer es opt-in (show-terms) y no se
  // hornea idioma dentro del componente — la app pasa los textos ya traducidos.
  termsPrefix: '',
  termsLinkText: '',
  termsConnector: '',
  privacyLinkText: '',
  showApple: true,
  showGoogle: true,
  // Footer de términos oculto por defecto: la app lo activa con show-terms.
  showTerms: false
};

// ============================================================================
// LOGOS (marcas de terceros — sizing vía clases del botón, no width/height fijo)
// ============================================================================

/** Glifo de Apple (blanco, currentColor) para el botón "Continua con Apple". */
export const APPLE_LOGO: string =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 814 1000" fill="currentColor"><path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"/></svg>';

/** Logo "G" multicolor oficial de Google para el botón "Continua con Google". */
export const GOOGLE_LOGO: string =
  '<svg viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg"><path fill="#4285F4" d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.615z"/><path fill="#34A853" d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.8059.54-1.8368.859-3.0477.859-2.344 0-4.3282-1.5831-5.036-3.7104H.9573v2.3318C2.4382 15.9832 5.4818 18 9 18z"/><path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.2822-1.1168-.2822-1.71s.1023-1.17.2822-1.71V4.9582H.9573A8.9965 8.9965 0 000 9c0 1.4523.3477 2.8268.9573 4.0418L3.964 10.71z"/><path fill="#EA4335" d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4632.9527 11.426 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.964 7.29C4.6718 5.1627 6.656 3.5795 9 3.5795z"/></svg>';

// ============================================================================
// CLASSES
// ============================================================================

export const BASE_CLASSES = {
  socialButton:
    'ec-h-14 ec-rounded-full ec-w-full ec-flex ec-items-center ec-justify-center ec-gap-2 ' +
    'ec-text-base ec-font-semibold ec-cursor-pointer ec-transition-colors ' +
    'hover:ec-brightness-95 disabled:ec-opacity-50 disabled:ec-cursor-not-allowed',
  apple: 'ec-bg-color-surface-inverse ec-text-white',
  google: 'ec-bg-color-surface ec-border ec-border-color-border ec-text-color-text',
  logo: 'ec-icon ec-w-6 ec-h-6 ec-flex-shrink-0',

  // ==========================================================================
  // RITMO VERTICAL (mockup) — escala 4/8/12/16/24/32/48.
  // Cada bloque trae su propio ec-mt-* de separación con el bloque anterior;
  // el root ya NO tiene gap uniforme (ver template.ts).
  // ==========================================================================

  /** Subtítulo → Apple: 24px. Apple → Google: 16px (gap interno). */
  socialGroup: 'ec-flex ec-flex-col ec-gap-4 ec-mt-6',
  /** Google → divisor "O con tu correo...": 32px. Centrado. */
  divider: 'ec-text-base ec-text-color-text ec-text-center ec-mt-8',
  /** Divisor → email: 24px. */
  form: 'ec-flex ec-flex-col ec-mt-6',
  /** Email → contraseña: 16px. */
  passwordSpacing: 'ec-mt-4',
  /** Contraseña → error: 8px. */
  errorSpacing: 'ec-mt-2',
  /** Error (si hay) / contraseña → botón Log in: 24px. */
  submitSpacing: 'ec-mt-6',
  /** Log in → "¿Olvidaste tu contraseña?": 24px. Centrado. */
  forgotLink:
    'ec-text-base ec-font-semibold ec-text-color-primary ec-self-center ec-cursor-pointer ec-mt-6',
  /** Forgot → <hr>: 40px. */
  hr: 'ec-border-t ec-border-color-border ec-mt-10',
  /** <hr> → "¿No tienes cuenta? Regístrate": 32px. Centrado. */
  registerLine: 'ec-text-center ec-text-base ec-text-color-text ec-mt-8',
  registerLink: 'ec-font-semibold ec-text-color-primary ec-underline ec-cursor-pointer',
  /** Register → términos: 24px. Centrado. */
  terms: 'ec-text-center ec-text-sm ec-text-color-text ec-mt-6',
  termsLink: 'ec-font-semibold ec-underline ec-cursor-pointer'
};

export default DEFAULTS;
